import type { PlatformEvent, EventFilter } from '../types/events';
import { levelDelivers } from './run-events-constants';

const RUN_EVENT_PREFIX = 'agent.run.';

/** Server cap on stored filters per connection. */
export const MAX_FILTERS = 100;

/**
 * Client-side filter matching for subscriptions. Mirrors the server-side
 * filter logic so handlers only receive events they subscribed for. This is
 * REQUIRED post-coalescing: the connection receives the union of every active
 * subscription's filters, so the matcher routes each event to only the handlers
 * that actually asked for it.
 *
 * - Empty filter `{}` matches all events.
 * - `eventType` supports trailing wildcards (e.g. `agent.run.*`). We use prefix
 *   matching (not segment-counting) deliberately: `agent.run.ask_user_*` is a
 *   partial-segment wildcard a segment-counter would break, and no current
 *   event needs deeper matching.
 * - `runId` matches against `event.subjectId` or `event.data.runId`.
 * - `subjectId` matches against `event.subjectId`.
 * - `conversationId` matches against `event.data.conversationId`.
 * - `level` gates `agent.run.*` events by detail tier (lifecycle ⊂ steps ⊂
 *   full); ignored for non-run events and bypassed when `runId` is set.
 * - All specified fields must match (AND). Any filter in the array matching
 *   is sufficient (OR across filters).
 */
export function matchesFilters(event: PlatformEvent, filters: EventFilter[]): boolean {
  if (filters.length === 0) return true;
  return filters.some((filter) => {
    if (filter.eventType) {
      if (filter.eventType.endsWith('*')) {
        const prefix = filter.eventType.slice(0, -1);
        if (!event.eventType?.startsWith(prefix)) return false;
      } else if (event.eventType !== filter.eventType) {
        return false;
      }
    }
    if (filter.source) {
      if (event.source !== filter.source) return false;
    }
    if (filter.runId) {
      const data = event.data as Record<string, unknown>;
      const eventRunId = event.subjectId ?? data?.runId;
      if (eventRunId !== filter.runId) {
        // When includeSubagents is set, also match events whose parentRunId
        // equals the filter's runId (sub-agent events for a coordinator run)
        if (filter.includeSubagents && data?.parentRunId === filter.runId) {
          // matched via parentRunId — continue
        } else {
          return false;
        }
      }
    }
    if (filter.subjectId) {
      if (event.subjectId !== filter.subjectId) return false;
    }
    if (filter.spaceId) {
      // Only deliver events from the requested space. Cross-space envelopes
      // (presence, org-level) carry no spaceId and are dropped from a
      // space-filtered subscription.
      if (event.spaceId !== filter.spaceId) return false;
    }
    if (filter.conversationId) {
      const data = event.data as Record<string, unknown>;
      if (data?.conversationId !== filter.conversationId) return false;
    }
    // Run-event detail tier. Only meaningful for `agent.run.*` events; a
    // `runId` filter always wants full detail, so it bypasses tier-gating.
    if (filter.level && filter.level !== 'full' && !filter.runId) {
      if (event.eventType?.startsWith(RUN_EVENT_PREFIX)) {
        const subtype = event.eventType.slice(RUN_EVENT_PREFIX.length);
        if (!levelDelivers(filter.level, subtype)) return false;
      }
    }
    return true;
  });
}

/** Fixed key order for stable filter serialization (also the dedup key). */
const FILTER_KEYS: (keyof EventFilter)[] = [
  'eventType', 'source', 'subjectId', 'runId',
  'includeSubagents', 'spaceId', 'conversationId', 'level',
];

/** Stable string key for an EventFilter — undefined fields dropped so
 *  `{eventType:'x'}` and `{eventType:'x', runId:undefined}` collapse. */
export function filterKey(f: EventFilter): string {
  const norm: Record<string, unknown> = {};
  for (const k of FILTER_KEYS) {
    const v = f[k];
    if (v !== undefined) norm[k] = v;
  }
  return JSON.stringify(norm);
}

/**
 * Flatten every subscription's filters into one deduped, deterministically
 * ordered array — the authoritative set sent via `{action:'set'}`. A filter is
 * present iff ≥1 live subscription references it (no refcounting needed). If any
 * subscription matches everything (`{}`), the union collapses to `[{}]` since
 * it supersets all other filters (and dodges the 100-filter cap).
 */
export function computeUnion(subs: readonly { filters: EventFilter[] }[]): EventFilter[] {
  const byKey = new Map<string, EventFilter>();
  for (const sub of subs) {
    for (const f of sub.filters) {
      const key = filterKey(f);
      if (key === '{}') return [{}];
      if (!byKey.has(key)) byKey.set(key, JSON.parse(key) as EventFilter);
    }
  }
  return [...byKey.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([, v]) => v);
}
